import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PortalCtx = createContext(null);

const authHeaders = () => {
  const t = localStorage.getItem("aa_portal_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const call = async (path) => {
  const res = await fetch(`${BACKEND}/api${path}`, { headers: authHeaders() });
  if (!res.ok) {
    const err = new Error(`${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

const post = async (path, body) => {
  const t = localStorage.getItem("aa_portal_token");
  const res = await fetch(`${BACKEND}/api${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
};

export const PortalProvider = ({ children }) => {
  const [me, setMe] = useState(null);
  const [tours, setTours] = useState([]);
  const [activeTourId, setActiveTourId] = useState(null);
  const [quote, setQuote] = useState(null);
  const [contract, setContract] = useState(null);
  const [billing, setBilling] = useState(null);
  const [routing, setRouting] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const m = await call("/portal/me");
      setMe(m);
      const tt = await call("/portal/tours");
      setTours(tt);
      if (tt.length > 0 && !activeTourId) setActiveTourId(tt[0].id);
    } catch (e) {
      const status = typeof e.status === "number" ? e.status : parseInt(e.message, 10);
      if (status === 401 || status === 403) {
        // Auth failure — session genuinely expired / invalid token.
        setError("Your secure session has ended — your account manager can send you a fresh link at any time.");
      } else {
        // Non-auth failure (500, network, etc.) — do NOT claim session expired.
        setError("Something went wrong loading your portal. Please try again or contact your account manager.");
      }
    } finally {
      setLoading(false);
    }
  }, [activeTourId]);

  /* Guard: prevent StrictMode double-invoke from firing a duplicate
     /portal/me request. The bootstrap gate in ClientPortal ensures the
     token is already in localStorage before this Provider mounts. */
  const didInitialLoad = useRef(false);
  useEffect(() => {
    if (didInitialLoad.current) return;
    didInitialLoad.current = true;
    load();
  }, []);

  useEffect(() => {
    if (!activeTourId) return;
    (async () => {
      try {
        setQuote(await call(`/portal/tours/${activeTourId}/quote`));
      } catch (e) {
        setQuote(null);
      }
      try {
        setDocs(await call(`/portal/tours/${activeTourId}/documents`));
      } catch (e) {
        setDocs([]);
      }
      try {
        setContract(await call(`/portal/tours/${activeTourId}/contract`));
      } catch (e) {
        setContract(null);
      }
      try {
        setBilling(await call(`/portal/tours/${activeTourId}/billing`));
      } catch (e) {
        setBilling(null);
      }
      try {
        setRouting(await call(`/portal/tours/${activeTourId}/routing`));
      } catch (e) {
        setRouting(null);
      }
    })();
  }, [activeTourId]);

  const refreshRouting = useCallback(async () => {
    if (!activeTourId) return null;
    try {
      const r = await call(`/portal/tours/${activeTourId}/routing`);
      setRouting(r);
      return r;
    } catch (e) {
      return null;
    }
  }, [activeTourId]);

  const submitRouting = useCallback(async (payload) => {
    if (!activeTourId) throw new Error("No active tour");
    // Server is the source of truth. Any failure surfaces to the caller
    // — do NOT falsely mark the submission as saved on the client.
    await post(`/portal/tours/${activeTourId}/routing/submission`, payload);
    await refreshRouting();
  }, [activeTourId, refreshRouting]);

  const refreshQuote = useCallback(async () => {
    if (!activeTourId) return;
    try {
      setQuote(await call(`/portal/tours/${activeTourId}/quote`));
    } catch (e) {
      /* noop */
    }
  }, [activeTourId]);

  const approveQuote = useCallback(async () => {
    if (!quote) throw new Error("No quote");
    await post(`/quotes/${quote.id}/status`, { status: "accepted" });
    await refreshQuote();
  }, [quote, refreshQuote]);

  const declineQuote = useCallback(async (reason) => {
    if (!quote) throw new Error("No quote");
    await post(`/quotes/${quote.id}/status`, { status: "declined", reason: reason || "" });
    await refreshQuote();
  }, [quote, refreshQuote]);

  const signOut = useCallback(() => {
    localStorage.removeItem("aa_portal_token");
    window.location.href = "/portal";
  }, []);

  const activeTour = tours.find((t) => t.id === activeTourId) || null;

  const value = {
    me, tours, activeTour, activeTourId, setActiveTourId,
    quote, contract, billing, routing, docs, loading, error,
    approveQuote, declineQuote, submitRouting, refreshRouting,
    signOut, refreshQuote,
  };

  return <PortalCtx.Provider value={value}>{children}</PortalCtx.Provider>;
};

export const usePortal = () => {
  const v = useContext(PortalCtx);
  if (!v) throw new Error("usePortal must be used within PortalProvider");
  return v;
};

/** In-progress-edit cache ONLY. Authoritative routing lives on the server
 *  and is fetched via GET /api/portal/tours/{id}/routing. The functions
 *  below are used only to preserve a user's unsaved textarea input across
 *  accidental navigation — they do NOT determine the submitted state.
 *  Cache is cleared as soon as a POST submission succeeds. */
const _cacheKey = (tourId) => `aa_portal_routing_draftcache_${tourId}`;

export const readRoutingDraftCache = (tourId) => {
  try {
    const raw = localStorage.getItem(_cacheKey(tourId));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const writeRoutingDraftCache = (tourId, payload) => {
  try {
    localStorage.setItem(_cacheKey(tourId), JSON.stringify(payload));
  } catch (e) { /* ignore */ }
};

export const clearRoutingDraftCache = (tourId) => {
  try {
    localStorage.removeItem(_cacheKey(tourId));
  } catch (e) { /* ignore */ }
};

// Legacy shims — kept temporarily so nothing crashes if an older bundle
// is still referenced. All new code MUST use the server routing endpoint.
export const readRoutingSubmission = () => null;
export const writeRoutingSubmission = () => { /* no-op — server is source of truth */ };
export const clearRoutingSubmission = () => { /* no-op */ };
